import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AttemptedPathService } from '@app/core/attempted-path.service';
import { REDEEM_MEMBERSHIP_FROM_EMAIL } from '@app/core/forgot-password/forgot-password.component';
import { CLAIM_CODE_PARAM_KEY } from '@app/shared/hornbill-params';

import { AnalyticsService } from './analytics.service';
import { AuthService, AuthServiceLoginArgs } from './auth.service';
import { FeatureFlags } from './feature-flags/feature-flags';
import { LaunchDarklyService } from './feature-flags/launchdarkly.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService implements CanActivate {
  private static readonly TYPE_NAME = 'AuthGuardService';
  private static readonly HIDE_LWA_PARAM_VALUE = 'true';

  constructor(
    private attemptedPathService: AttemptedPathService,
    private authService: AuthService,
    private analyticsService: AnalyticsService,
    private launchDarklyService: LaunchDarklyService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.authService.isLoggedIn$().pipe(
      map(isLoggedIn => isLoggedIn || this.handleUnauthenticated(state.url, route)),
      catchError(() => observableOf(this.handleUnauthenticated(state.url, route))),
    );
  }

  private handleUnauthenticated(url: string, route: ActivatedRouteSnapshot): boolean {
    url = url.replace('login_with_amazon=false', '');
    if (url.includes('/account/security?dismiss=true')) {
      url = '/';
    }
    this.analyticsService.trackDirectLinksToLoggedInResources(url);
    this.attemptedPathService.setAttemptedPath(url);

    const loginWithAmazonParam = route.queryParams['login_with_amazon'];
    const hideLWAParam = route.queryParams['hideLWA'];

    // eslint-disable-next-line no-console
    console.log('AUTH-GUARD:', url, 'hideLWA:', hideLWAParam, 'type:', typeof hideLWAParam);

    let loginArgs: AuthServiceLoginArgs = {
      path: url,
      trackingSource: AuthGuardService.TYPE_NAME,
    };

    const customAuthorizationParams: any = {};

    if (loginWithAmazonParam) {
      customAuthorizationParams.login_with_amazon = loginWithAmazonParam;
    }

    // Only add hideLWA if feature flag is enabled
    this.launchDarklyService
      .featureFlag$(FeatureFlags.HIDE_LWA, false)
      .pipe(catchError(() => observableOf(false)))
      .subscribe(hideLWAFeatureEnabled => {
        // eslint-disable-next-line no-console
        console.log(
          'FEATURE-FLAG:',
          hideLWAFeatureEnabled,
          'CONDITION:',
          hideLWAFeatureEnabled && hideLWAParam === AuthGuardService.HIDE_LWA_PARAM_VALUE,
        );

        if (hideLWAFeatureEnabled && hideLWAParam === AuthGuardService.HIDE_LWA_PARAM_VALUE) {
          // eslint-disable-next-line no-console
          console.log('ADDING hideLWA=true to Auth0');
          customAuthorizationParams.hideLWA = AuthGuardService.HIDE_LWA_PARAM_VALUE;
        }

        if (Object.keys(customAuthorizationParams).length > 0) {
          loginArgs = { ...loginArgs, customAuthorizationParams };
        }

        if (!!route.queryParams[CLAIM_CODE_PARAM_KEY]) {
          loginArgs = { ...loginArgs, fragment: REDEEM_MEMBERSHIP_FROM_EMAIL };
        }

        this.authService.goLogin(loginArgs);
      });

    return false;
  }
}
